import { useCallback, useEffect, useRef } from "react";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import {
  AdAttribute,
  AdState,
  IrisEvents,
  PerformanceEvents,
  PromoState,
  VideoEvents
} from "../../../globals";
import { addOrcaEvent } from "../../utils";
import {
  getAdAttribute,
  getAdCompleteAttribute,
  getAvAdMeta,
  getSkipData
} from "../utils/helpers";
import { dispatchNewEvent } from "../../utils/elementEvents";
import { VideoEvent } from "../../../lib/analytics/utils";
import { useVideoElementRefContext } from "../../contexts/VideoElementRefContext";
import { useVideoAnalyticsContext } from "../../contexts/VideoAnalyticsContext";
import { useStickyVideoContext } from "../../contexts/StickyVideoContext";
import { usePerformanceContext } from "../../contexts/PerformanceContext";
import {
  AdActionType,
  useAdControlsContext
} from "../contexts/AdControlsContext";
import { useAdsElementsRefContext } from "../../contexts/AdsElementsRefContext";

const useIrisEventListeners = ({
  adSettings,
  debug,
  transitionToVideo,
  adState,
  setAdState,
  setPromoState,
  setRenderControls,
  playerState,
  updateDisplay,
  isAdsInitialized,
  pauseVideo = () => {}
}) => {
  const videoElementRef = useVideoElementRefContext();
  const { videoData, videoId, uniqueId } = useVideoDataContext();
  const { dispatchAdAction, mute, skip, getIrisPlayerData, reset } =
    useAdControlsContext();
  const { sendAnalyticsEvent, setAvAdMeta, setAdAttribute } =
    useVideoAnalyticsContext();
  const { updateStickyContext, isStickyEnabled } = useStickyVideoContext();
  const { sendPerformanceEvent, sendPerformanceMeasure } =
    usePerformanceContext();
  const { adWrapperRef } = useAdsElementsRefContext();

  const {
    current: { debugZeus, isPreroll, videoConfig: { skipmin } = {} } = {}
  } = adSettings;
  const { isMuted } = playerState;

  const handleRequestAd = useCallback(() => {
    addOrcaEvent(IrisEvents.REQUEST, true, videoId, debug);
    const requestEvent = new Event(VideoEvents.AD_REQUESTED);
    videoElementRef.current.dispatchEvent(requestEvent);
  }, [videoId, debug, videoElementRef]);

  const handleLoad = useCallback(() => {
    setAdState(AdState.LOADED);
    addOrcaEvent(IrisEvents.LOADED, true, videoId, debug);
  }, [debug, setAdState, videoId]);

  const handleStart = useCallback(() => {
    const { adData } = getIrisPlayerData();
    const { duration } = adData;

    if (debugZeus) {
      console.log("Iris Event", IrisEvents.START);
    }
    // When the ad starts, hide the promo.
    setPromoState(PromoState.HIDDEN);

    // If the video is in sticky mode, update the videoId.
    if (isStickyEnabled) {
      updateStickyContext({ playingId: uniqueId });
    }

    // Set the playing state, don't render controls, set ad state, if its muted
    // update the mute to match player.
    dispatchAdAction({ type: AdActionType.PLAY });
    setRenderControls(false);
    setAdState(AdState.PLAYING);
    if (isMuted || isPreroll) {
      mute();
    }

    // If the video tries to play at the same time as the ad, we pause so audio doesn't overlap
    pauseVideo();

    // Set countdown for skip, and send splunk event for ad start.
    dispatchAdAction({
      type: AdActionType.SET_SHOW_COUNTDOWN,
      showCountdown: skipmin ? duration >= skipmin : duration >= 30
    });
    dispatchNewEvent(videoElementRef.current, VideoEvents.AD_START, adData);

    // Update display for walls interaction.
    updateDisplay({ state: "media-start" });

    const skipTime = adWrapperRef.current.irisPlayer?.getSkipTime();
    const adAttribute = getAdAttribute(adData);
    const avAdMeta = getAvAdMeta(adData, skipTime);
    setAvAdMeta(avAdMeta);
    setAdAttribute(adAttribute);

    if (duration > 1) {
      sendAnalyticsEvent(VideoEvent.AD_START, {
        avAdMeta,
        adAttribute
      });
      sendPerformanceEvent(PerformanceEvents.AD_START);
      sendPerformanceMeasure(
        PerformanceEvents.AD_START,
        PerformanceEvents.PROMO_CLICK
      );
      addOrcaEvent(IrisEvents.START, true, videoId, debug);
      dispatchAdAction({
        type: AdActionType.SET_SHOW_CONTROLS,
        showControls: true
      });
    }
  }, [
    getIrisPlayerData,
    debugZeus,
    setPromoState,
    isStickyEnabled,
    dispatchAdAction,
    setRenderControls,
    setAdState,
    isMuted,
    videoElementRef,
    updateDisplay,
    updateStickyContext,
    videoId,
    mute,
    debug,
    adWrapperRef,
    setAvAdMeta,
    sendAnalyticsEvent,
    pauseVideo,
    uniqueId,
    sendPerformanceEvent,
    sendPerformanceMeasure,
    setAdAttribute,
    isPreroll
  ]);

  const handlePlay = useCallback(() => {
    addOrcaEvent(IrisEvents.PLAY, true, videoId, debug);
    dispatchAdAction({ type: AdActionType.PLAY });
  }, [debug, dispatchAdAction, videoId]);

  const handlePause = useCallback(() => {
    addOrcaEvent(IrisEvents.PAUSE, true, videoId, debug);
    dispatchAdAction({ type: AdActionType.PAUSE });
  }, [debug, dispatchAdAction, videoId]);

  const handleTimeChange = useCallback(
    e => {
      const skipTimeOffset = adWrapperRef.current.irisPlayer.getSkipTime();
      const { remainingTime, currentTime } = e;
      const skipData = getSkipData(remainingTime, currentTime, skipTimeOffset); // Set the remaining time, countdown for skipping ad.
      dispatchAdAction({
        type: AdActionType.HANDLE_TIME_CHANGE,
        skipData
      });
    },
    [dispatchAdAction, adWrapperRef]
  );

  const handleSkipReady = useCallback(() => {
    dispatchAdAction({ type: AdActionType.SKIP_READY });
    addOrcaEvent(IrisEvents.SKIP_READY, true, videoId, debug);
  }, [dispatchAdAction, debug, videoId]);

  const handleError = useCallback(
    e => {
      const { adData } = getIrisPlayerData();
      addOrcaEvent(IrisEvents.ERROR, e, videoId, debug);
      setAdState(AdState.ERROR);
      reset();

      setAdAttribute(AdAttribute.ERROR);
      transitionToVideo({ analyticsData: { adAttribute: AdAttribute.ERROR } });
      dispatchNewEvent(videoElementRef.current, VideoEvents.AD_ERROR, adData);
    },
    [
      getIrisPlayerData,
      videoId,
      debug,
      setAdState,
      reset,
      setAdAttribute,
      transitionToVideo,
      videoElementRef
    ]
  );

  const handleComplete = useCallback(() => {
    const { adData } = getIrisPlayerData();
    const { duration } = adData;
    addOrcaEvent(AdAttribute.COMPLETE, true, videoId, debug);
    setAdState(AdState.COMPLETE);
    reset();

    const adAttribute = getAdCompleteAttribute(adData);

    setAdAttribute(adAttribute);
    dispatchNewEvent(videoElementRef.current, VideoEvents.AD_COMPLETE, adData);

    if (duration > 1) {
      sendAnalyticsEvent(VideoEvent.AD_COMPLETE, {
        avAdMeta: getAvAdMeta(adData),
        adAttribute
      });
    }
    transitionToVideo({ analyticsData: { adAttribute } });
  }, [
    debug,
    getIrisPlayerData,
    reset,
    sendAnalyticsEvent,
    setAdAttribute,
    setAdState,
    transitionToVideo,
    videoElementRef,
    videoId
  ]);

  const handleBypassed = useCallback(
    e => {
      addOrcaEvent(IrisEvents.BYPASSED, e, videoId, debug);
      setAdState(AdState.BYPASSED);
      reset();

      setAdAttribute(AdAttribute.DISABLED);
      transitionToVideo({
        analyticsData: { adAttribute: AdAttribute.DISABLED }
      });
      dispatchNewEvent(videoElementRef.current, VideoEvents.AD_BYPASSED);
    },
    [
      videoId,
      debug,
      setAdState,
      reset,
      setAdAttribute,
      transitionToVideo,
      videoElementRef
    ]
  );

  const { startTimer, stopTimer } = useAdPlayTimer({
    debug,
    adWrapperRef,
    transitionToVideo,
    setAdState
  });

  useEffect(() => {
    if (adState === AdState.REQUESTED) {
      // start the timer when the play button is clicked or autoplay is triggered
      startTimer();
    } else if (
      [
        AdState.PLAYING,
        AdState.COMPLETE,
        AdState.ERROR,
        AdState.BYPASSED,
        AdState.TIMEOUT
      ].includes(adState)
    ) {
      // stop the timer when the ad is playing, complete, error, bypassed, or timeout
      stopTimer();
    }
  }, [adState, startTimer, stopTimer]);

  useEffect(() => {
    if (!isAdsInitialized) return;

    const { irisPlayer } = adWrapperRef.current || {};

    if (!irisPlayer) return;

    irisPlayer.on(IrisEvents.REQUEST, handleRequestAd);
    irisPlayer.on(IrisEvents.LOAD, handleLoad);
    irisPlayer.on(IrisEvents.START, handleStart);
    irisPlayer.on(IrisEvents.PLAY, handlePlay);
    irisPlayer.on(IrisEvents.PAUSE, handlePause);
    irisPlayer.on(IrisEvents.TIME_CHANGE, handleTimeChange);
    irisPlayer.on(IrisEvents.SKIP_READY, handleSkipReady);
    irisPlayer.on(IrisEvents.ERROR, handleError);
    irisPlayer.on(IrisEvents.COMPLETE, handleComplete);
    irisPlayer.on(IrisEvents.BYPASSED, handleBypassed);
    return () => {
      irisPlayer.off(IrisEvents.REQUEST, handleRequestAd);
      irisPlayer.off(IrisEvents.LOAD, handleLoad);
      irisPlayer.off(IrisEvents.START, handleStart);
      irisPlayer.off(IrisEvents.PLAY, handlePlay);
      irisPlayer.off(IrisEvents.PAUSE, handlePause);
      irisPlayer.off(IrisEvents.TIME_CHANGE, handleTimeChange);
      irisPlayer.off(IrisEvents.SKIP_READY, handleSkipReady);
      irisPlayer.off(IrisEvents.ERROR, handleError);
      irisPlayer.off(IrisEvents.COMPLETE, handleComplete);
      irisPlayer.off(IrisEvents.BYPASSED, handleBypassed);
    };
  }, [
    adWrapperRef,
    handleLoad,
    handleRequestAd,
    handleStart,
    videoData,
    handlePlay,
    handleTimeChange,
    handleSkipReady,
    handleError,
    handleComplete,
    isAdsInitialized,
    handlePause,
    handleBypassed
  ]);

  return skip;
};

// This is the play timer for the ad after the ad has been requested/played
export const useAdPlayTimer = ({
  debug,
  adWrapperRef,
  transitionToVideo,
  setAdState
}) => {
  const waitTime = 6000;
  const adTimer = useRef(null);
  const videoElementRef = useVideoElementRefContext();
  const { reset } = useAdControlsContext();
  const { setAdAttribute } = useVideoAnalyticsContext();
  const { videoId } = useVideoDataContext();

  const handleTimeout = useCallback(() => {
    addOrcaEvent(
      VideoEvents.AD_TIMEOUT,
      { message: "Ad Play Timeout" },
      videoId,
      debug
    );
    setAdState(AdState.TIMEOUT);
    try {
      // try to stop the video, but this depends on states of the video
      adWrapperRef.current?.irisPlayer?.stop();
    } catch (err) {
      if (debug) console.log("failed to stop the video", err);
    }

    reset();
    setAdAttribute(AdAttribute.DISABLED);
    transitionToVideo({ analyticsData: { adAttribute: AdAttribute.DISABLED } });
    dispatchNewEvent(videoElementRef.current, VideoEvents.AD_TIMEOUT);
  }, [
    debug,
    videoId,
    setAdState,
    adWrapperRef,
    reset,
    setAdAttribute,
    transitionToVideo,
    videoElementRef
  ]);

  const startTimer = useCallback(() => {
    if (adTimer.current !== null) {
      return;
    }
    adTimer.current = setTimeout(() => {
      handleTimeout();
      adTimer.current = null;
    }, waitTime);
  }, [handleTimeout]);

  const stopTimer = useCallback(() => {
    if (adTimer.current === null) {
      return;
    }
    clearTimeout(adTimer.current);
    adTimer.current = null;
  }, [adTimer]);

  return { adTimer, startTimer, stopTimer };
};

export default useIrisEventListeners;
